import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  TypedLazyQueryTrigger,
} from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "..";
import {
  BusinessSelectorResponse,
  NavigationApiResponse,
  NavigationRequestParams,
  ShellConfigApiResponse,
} from "./mybizShellAPI.types";

/**
 * Validates and normalizes personaV2 value from API response
 * @param personaV2 - The personaV2 value from API response
 * @returns A valid personaV2 value (1, 2, or 3), defaulting to 1 if invalid
 */
const validatePersonaV2 = (personaV2: unknown): number => {
  if (typeof personaV2 === "number" && [1, 2, 3].includes(personaV2)) {
    return personaV2;
  }

  // Log warning for monitoring if personaV2 is invalid or missing
  if (personaV2 === undefined || personaV2 === null) {
    console.warn("[PersonaV2 Validation] personaV2 is missing from API response, defaulting to 1");
  } else if (typeof personaV2 !== "number") {
    console.error(
      "[PersonaV2 Validation] personaV2 has invalid type:",
      typeof personaV2,
      "value:",
      personaV2,
      "defaulting to 1",
    );
  } else {
    console.error("[PersonaV2 Validation] personaV2 is out of valid range [1, 2, 3]:", personaV2, "defaulting to 1");
  }

  return 1;
};

export type MyBizShellRequestParams = {
  abo: string;
  aff: string;
  period: string;
  faaEligibilityWindow?: number;
  optionalPeriodRange?: number;
};

type BusinessSelectorParams = {
  aff: string;
  abo: string;
};

export const mybizShellAPI = createApi({
  reducerPath: "shellConfig",
  baseQuery: dynamicBaseQuery,
  endpoints: (builder) => ({
    getMyBizShellConfig: builder.query<ShellConfigApiResponse, MyBizShellRequestParams>({
      query: (params) => {
        const { abo, aff, period, faaEligibilityWindow = 12, optionalPeriodRange } = params;
        const queryParams = new URLSearchParams({
          Abo: abo,
          Aff: aff,
          Period: period,
          FaaEligibilityWindow: faaEligibilityWindow.toString(),
        });

        if (optionalPeriodRange) queryParams.append("OptionalPeriodRange", `${optionalPeriodRange}`);

        return `/mybiz-shell/api/config?${queryParams}`;
      },
      transformResponse: (response: ShellConfigApiResponse) => {
        // Validate and normalize personaV2 value
        return {
          ...response,
          personaV2: validatePersonaV2(response.personaV2),
        };
      },
      // keep data for 1 hour
      keepUnusedDataFor: 3600,
    }),

    getBusinessSelector: builder.query<BusinessSelectorResponse, BusinessSelectorParams>({
      query: (params) => {
        const { abo, aff } = params;
        const queryParams = new URLSearchParams({
          Abo: abo,
          Aff: aff,
        });

        return `/mybiz-shell/api/business-selector?${queryParams}`;
      },
    }),

    getNavigation: builder.query<NavigationApiResponse, NavigationRequestParams>({
      query: (params) => {
        const { aff, abo, period, isoCountryCode } = params;
        const queryParams = new URLSearchParams({
          Aff: aff,
          Abo: abo,
          Period: period,
          IsoCountryCode: isoCountryCode,
        });

        return `/mybiz-shell/api/navigation?${queryParams}`;
      },
    }),
  }),
});

export type LazyGetMyBizShellConfigQueryType = TypedLazyQueryTrigger<
  ShellConfigApiResponse,
  MyBizShellRequestParams,
  BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>
>;

export type LazyGetBusinessSelectorQueryType = TypedLazyQueryTrigger<
  BusinessSelectorResponse,
  BusinessSelectorParams,
  BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>
>;

export const {
  useGetMyBizShellConfigQuery,
  useLazyGetMyBizShellConfigQuery,
  useGetBusinessSelectorQuery,
  useLazyGetBusinessSelectorQuery,
  useGetNavigationQuery,
} = mybizShellAPI;
